<template>
  <transition-group
    tag="ul"
    :class="[
      nsUpload.b('list'),
      nsUpload.bm('list', listType),
      nsUpload.is('disabled', disabled),
      nsUpload.is('empty', isEmpty),
    ]"
    :name="nsList.b()"
  >
    <li
      v-for="file in files"
      :key="file.uid || file.name"
      :class="[
        nsUpload.be('list', 'item'),
        nsUpload.be('list', `item-${listType}`),
        nsUpload.is(file.status),
        nsUpload.is('small', isSmall),
        { focusing },
      ]"
      tabindex="0"
      @keydown.delete="!disabled && handleRemove(file)"
      @focus="focusing = true"
      @blur="focusing = false"
      @click="focusing = false"
    >
      <slot :file="file">
        <div
          v-if="
            fileListIconSlot && !file?.type?.includes('image') && listType === 'picture'
          "
          :class="nsUpload.be('list', 'item-thumbnail')"
        >
          <slot
            name="file-list-icon"
            :file="file"
            :is-small="isSmall"
            :list-type="listType"
          />
        </div>
        <fallback-img
          v-else-if="
            listType === 'picture' ||
            (file.status !== 'uploading' && listType === 'picture-card')
          "
          :class="nsUpload.be('list', 'item-thumbnail')"
          :src="file.url"
          alt=""
          :status="file.status"
        />
        <div
          v-if="
            listType !== 'picture' &&
            (file.status === 'uploading' || listType !== 'picture-card')
          "
          :class="nsUpload.be('list', 'item-info')"
        >
          <a
            :class="nsUpload.be('list', 'item-name')"
            @click.prevent="handlePreview(file)"
          >
            <!-- <jd-icon :class="nsIcon.m('document')">
              <Document />
            </jd-icon> -->
            <span
              :class="nsUpload.be('list', 'item-file-name')"
              :title="file.name"
            >
              {{ file.name }}
            </span>
          </a>
          <jd-progress
            v-if="file.status === 'uploading'"
            :type="listType === 'picture-card' ? 'circle' : 'line'"
            :stroke-width="listType === 'picture-card' ? 6 : 2"
            :percentage="Number(file.percentage)"
            :show-text="listType === 'picture-card' ? true : false"
            :width="100"
            :status="Number(file.percentage) >= 100 ? 'success' : ''"
          />
        </div>
        <div
          v-if="listType === 'picture'"
          :title="file.name"
          :class="nsUpload.be('list', 'item-file-name')"
        >
          <div :class="nsUpload.be('list', 'item-file-name-label')">
            <div :class="nsUpload.be('list', 'item-file-name-label-name')">
              {{ file.name }}
            </div>
            <span
              v-if="file.status === 'success' && file.size && !isSmall"
              :class="nsUpload.be('list', 'item-file-size')"
            >
              {{ getFileSizeText(file.size) }}
            </span>
            <span
              v-if="
                file.status === 'fail' &&
                file.response &&
                typeof file.response === 'string' &&
                !isSmall
              "
              :class="nsUpload.be('list', 'item-file-error-text')"
            >
              {{ file.response }}
            </span>
          </div>
          <jd-progress
            v-if="file.status === 'uploading'"
            type="line"
            :stroke-width="isSmall ? 2 : 6"
            :show-text="false"
            :percentage="Number(file.percentage)"
            :status="Number(file.percentage) >= 100 ? 'success' : ''"
          />
        </div>

        <label :class="nsUpload.be('list', 'item-status-label')">
          <!-- <jd-icon
            v-if="['picture', 'text'].includes(listType)"
            :class="[nsIcon.m('upload-success'), nsIcon.m('circle-check')]"
          >
            <circle-check />
          </jd-icon> -->
          <div
            v-if="listType === 'picture' && file.status === 'uploading'"
            :class="nsUpload.be('list', 'item-file-name-percentage')"
          >
            {{ Math.floor(Number(file.percentage)) }}%
          </div>
          <jd-icon
            v-if="
              ['picture', 'text'].includes(listType) && file.status === 'fail'
            "
            :class="[nsIcon.m('upload-fail'), nsIcon.m('circle-exclamation')]"
          >
            <exclamation-circle-f />
          </jd-icon>
        </label>
        <label
          v-if="listType === 'text' && file.status === 'uploading'"
          :class="nsUpload.be('list', 'item-status-percentage')"
        >
          <div :class="nsUpload.be('list', 'item-file-name-percentage')">
            {{ Math.floor(Number(file.percentage)) }}%
          </div>
        </label>
        <jd-icon
          v-if="!disabled"
          :class="nsIcon.m('close')"
          @click="handleRemove(file)"
        >
          <Trash />
        </jd-icon>
        <!-- Due to close btn only appears when li gets focused disappears after li gets blurred, thus keyboard navigation can never reach close btn-->
        <!-- This is a bug which needs to be fixed -->
        <!-- TODO: Fix the incorrect navigation interaction -->
        <!-- <i v-if="!disabled" :class="nsIcon.m('close-tip')">{{
          t('el.upload.deleteTip')
        }}</i> -->
        <span
          v-if="listType === 'picture-card'"
          :class="nsUpload.be('list', 'item-actions')"
        >
          <span
            v-if="!disabled"
            :class="nsUpload.be('list', 'item-delete')"
            @click="handleRemove(file)"
          >
            <jd-icon :class="nsIcon.m('delete')"><trash /></jd-icon>
          </span>
          <span
            :class="nsUpload.be('list', 'item-preview')"
            @click="handlePreview(file)"
          >
            <jd-icon :class="nsIcon.m('zoom-in')"><zoom-in /></jd-icon>
          </span>
        </span>
      </slot>
    </li>
    <slot name="append" />
  </transition-group>
</template>
<script lang="ts" setup>
import { computed, ref, useSlots } from 'vue'
import {
  // CheckCircleF as CircleCheck,
  // Close,
  ExclamationCircleF,
  Trash,
  ZoomIn,
} from '@jd/jdesign-icons-vue'
import { JdIcon } from '@jdesign-vue/components/icon'
import { useLocale, useNamespace } from '@jdesign-vue/hooks'
import JdProgress from '@jdesign-vue/components/progress'
import { useFormDisabled } from '@jdesign-vue/components/form'
import FallbackImg from './components/fallback-img.vue'
import { getFileSizeText } from './utils'

import { uploadListEmits, uploadListProps } from './upload-list'
import type { UploadFile } from './upload'

defineOptions({
  name: 'JdUploadList',
})

const props = defineProps(uploadListProps)
const emit = defineEmits(uploadListEmits)
const slots = useSlots()
const fileListIconSlot = slots['file-list-icon']

const { t } = useLocale()
const nsUpload = useNamespace('upload')
const nsIcon = useNamespace('icon')
const nsList = useNamespace('list')
const disabled = useFormDisabled()

const focusing = ref(false)

const isEmpty = computed(() => props.files.length <= 0)

const isSmall = computed(() => props.listSize === 'small')

const handleRemove = (file: UploadFile) => {
  emit('remove', file)
}
</script>
